import useTakeSnapshot from 'pages/Editor/sections/Flow/hooks/useTakeSnapshot';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useReactFlow } from 'reactflow';
import { BlockData, ImageFormValues } from 'types';
import { deepCopy } from 'utils/commonUtils';

interface UseImageDataFormParams {
  id: string;
  blockData: BlockData;
}

const useImageDataForm = ({ id, blockData }: UseImageDataFormParams) => {
  const [data, setData] = useState(blockData);
  const defaultValues = useMemo(() => createDefaultValues(blockData), [blockData]);
  const { takeSnapshot } = useTakeSnapshot();

  const { getNodes, setNodes } = useReactFlow();
  const useFormReturn = useForm<ImageFormValues>({
    defaultValues,
  });

  const nodeDataSave = () => {
    const values = useFormReturn.getValues();

    const parameters = {
      target: values.target,
      csv_name: values.target === 'csv' ? values.csvName : null,
      train_input_preprocessings: values.trainInputPreprocessings,
      test_input_preprocessings: values.testInputPreprocessings,
      train_target_preprocessings: values.target !== 'folder' ? values.trainTargetPreprocessings : null,
    } as Record<string, any>;

    const copiedParameters = deepCopy<Record<string, any>>(parameters);

    const newDataParameters = data.parameters.map((parameter) => ({
      ...parameter,
      value: copiedParameters[parameter.param],
    }));

    const newNodes = getNodes().map((node) => {
      if (node.id === id) {
        return {
          ...node,
          data: {
            ...node.data,
            name: values.name,
            parameters: newDataParameters,
          },
        };
      }
      return node;
    });

    setNodes(newNodes);
    setData((prev) => ({ ...prev, parameters: newDataParameters }));
    takeSnapshot({ undoRedo: false });
  };

  return {
    useFormReturn,
    nodeDataSave,
  };
};

export default useImageDataForm;

type CreateDefaultValues = (data: BlockData) => ImageFormValues;

const createDefaultValues: CreateDefaultValues = (data) => {
  const parameterInfo = {} as Record<string, { value: any; defaultValue: any }>;
  data.parameters.forEach((parameter) => {
    parameterInfo[parameter.param] = {
      value: parameter.value,
      defaultValue: parameter.defaultValue,
    };
  });

  const trainInputPreprocessings = parameterInfo.train_input_preprocessings;
  const trainTargetPreprocessings = parameterInfo.train_target_preprocessings;
  const testInputPreprocessings = parameterInfo.test_input_preprocessings;

  return {
    name: data.name,
    target: parameterInfo.target.value,
    csvName: parameterInfo.csv_name.value ?? '',
    trainInputPreprocessings: trainInputPreprocessings.value,
    testInputPreprocessings: testInputPreprocessings.value,
    trainTargetPreprocessings: trainTargetPreprocessings.value ?? [],
  };
};
